/**
 * Services (Servizi) — catalog page with category grouping, search/sort,
 * card/table view toggle, archive-instead-of-delete, and "starting-at" price.
 * Inspired by Fresha's catalog UX.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus, X, Pencil, Scissors, Gift, Search, Archive, ArchiveRestore,
  ChevronDown, ChevronRight, LayoutGrid, Rows, Trash2, AlertTriangle,
  Package as PackageIcon, Sparkles,
} from "lucide-react";
import SortPopover from "@/components/SortPopover";
import { SearchableSelect } from "@/components/SearchableSelect";
import { ColorSwatchGrid, DEFAULT_PALETTE } from "@/components/ColorSwatchGrid";

const PALETTE = DEFAULT_PALETTE;
const SORT_FIELDS = [
  { key: "name",        label: "Nome" },
  { key: "price",       label: "Prezzo" },
  { key: "duration_min", label: "Durata" },
  { key: "category",    label: "Categoria" },
];
const NO_CAT = "Senza categoria";

export default function Services() {
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("services");
  // Which category header has its color-picker popover open
  const [catPickerOpen, setCatPickerOpen] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const [showPkgForm, setShowPkgForm] = useState(false);
  const [editingPkg, setEditingPkg] = useState(null);
  const [pkgForm, setPkgForm] = useState(emptyPkg());

  // Confirm dialog state — reusable across delete flows
  const [confirmState, setConfirmState] = useState(null); // { title, message, danger?, onConfirm }

  // Toolbar state
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("category");
  const [sortDir, setSortDir] = useState("asc");
  const [view, setView] = useState("cards"); // cards | table
  const [showArchived, setShowArchived] = useState(false);
  const [collapsed, setCollapsed] = useState({});

  const load = async () => {
    const [r1, r2, r3, r4] = await Promise.all([
      api.get("/services?include_archived=true"),
      api.get("/packages").catch(() => ({ data: [] })),
      api.get("/products").catch(() => ({ data: [] })),
      api.get("/service-categories").catch(() => ({ data: [] })),
    ]);
    setItems(r1.data);
    setPackages(r2.data || []);
    setProducts(r3.data || []);
    setCategories(r4.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  // -- Service form -------------------------------------------------------
  // NOTE: services no longer carry their own color picker. Colors are owned by
  // categories (service_categories collection) and auto-inherited via backend.
  function emptyForm() {
    return { name: "", duration_min: 30, price: "", price_type: "fixed", category: "", description: "" };
  }
  const openForm = (s) => {
    setEditing(s || null);
    setForm(s ? {
      name: s.name, duration_min: s.duration_min, price: s.price,
      price_type: s.price_type || "fixed",
      category: s.category || "", description: s.description || "",
    } : emptyForm());
    setShowForm(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      duration_min: Number(form.duration_min),
      price: Number(form.price),
    };
    try {
      if (editing) await api.patch(`/services/${editing.id}`, payload);
      else await api.post("/services", payload);
      toast.success("Servizio salvato");
      setShowForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  // Direct archive with undo toast — no browser confirm
  const archiveSvc = async (s) => {
    try {
      await api.post(`/services/${s.id}/archive`);
      load();
      toast.success(`"${s.name}" archiviato`, {
        action: {
          label: "Annulla",
          onClick: async () => {
            try { await api.post(`/services/${s.id}/restore`); load(); toast("Ripristinato"); }
            catch { toast.error("Errore ripristino"); }
          },
        },
      });
    } catch { toast.error("Errore archiviazione"); }
  };
  const restoreSvc = async (s) => {
    try {
      await api.post(`/services/${s.id}/restore`);
      toast.success(`"${s.name}" riattivato`);
      load();
    } catch { toast.error("Errore"); }
  };
  const permanentDelete = (s) => {
    setConfirmState({
      title: "Elimina definitivamente",
      message: (
        <>
          Vuoi davvero eliminare <b>&ldquo;{s.name}&rdquo;</b>?
          <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-800 flex gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div>
              <b>Attenzione:</b> incassi, statistiche e dati di performance già legati a questo servizio andranno persi. Le righe di incasso storiche restano ma non saranno più collegate al servizio.
              <br />L&apos;operazione è <b>irreversibile</b>.
            </div>
          </div>
        </>
      ),
      danger: true,
      confirmLabel: "Elimina definitivamente",
      onConfirm: async () => {
        try {
          await api.delete(`/services/${s.id}`);
          toast.success(`"${s.name}" eliminato`);
          load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
      },
    });
  };

  // -- Package form -------------------------------------------------------
  function emptyPkg() {
    return { name: "", description: "", items: [], bonus_items: [], promo_price: "", color: PALETTE[3] };
  }
  const openPkgForm = (p) => {
    setEditingPkg(p || null);
    if (p) {
      // Backward-compat: if items empty, hydrate from legacy service_ids
      const hydratedItems = (p.items && p.items.length)
        ? p.items
        : (p.service_ids || []).map((sid) => {
            const s = items.find((x) => x.id === sid);
            return { kind: "service", ref_id: sid, name: s?.name || "—", quantity: 1, unit_price: s?.price || 0 };
          });
      const hydratedBonus = (p.bonus_items && p.bonus_items.length)
        ? p.bonus_items
        : (p.bonus ? [{ kind: "other", ref_id: null, name: p.bonus }] : []);
      setPkgForm({
        name: p.name || "",
        description: p.description || "",
        items: hydratedItems,
        bonus_items: hydratedBonus,
        promo_price: p.promo_price ?? "",
        color: p.color || PALETTE[3],
      });
    } else {
      setPkgForm(emptyPkg());
    }
    setShowPkgForm(true);
  };

  // Package cart helpers
  const addPkgItem = (kind) => setPkgForm((f) => ({ ...f, items: [...f.items, { kind, ref_id: null, name: "", quantity: 1, unit_price: 0 }] }));
  const updatePkgItem = (i, patch) => setPkgForm((f) => ({ ...f, items: f.items.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const removePkgItem = (i) => setPkgForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const onPkgItemPick = (i, refId) => {
    const l = pkgForm.items[i];
    if (l.kind === "service") {
      const s = items.find((x) => x.id === refId);
      if (!s) { updatePkgItem(i, { ref_id: null }); return; }
      updatePkgItem(i, { ref_id: s.id, name: s.name, unit_price: s.price });
    } else if (l.kind === "product") {
      const p = products.find((x) => x.id === refId);
      if (!p) { updatePkgItem(i, { ref_id: null }); return; }
      updatePkgItem(i, { ref_id: p.id, name: p.name, unit_price: p.retail_price || p.unit_cost || 0 });
    }
  };

  const addBonus = (kind) => setPkgForm((f) => ({ ...f, bonus_items: [...f.bonus_items, { kind, ref_id: null, name: "", quantity: 1, unit_price: 0 }] }));
  const updateBonus = (i, patch) => setPkgForm((f) => ({ ...f, bonus_items: f.bonus_items.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const removeBonus = (i) => setPkgForm((f) => ({ ...f, bonus_items: f.bonus_items.filter((_, idx) => idx !== i) }));
  const onBonusPick = (i, refId) => {
    const l = pkgForm.bonus_items[i];
    if (l.kind === "service") {
      const s = items.find((x) => x.id === refId);
      if (!s) { updateBonus(i, { ref_id: null }); return; }
      updateBonus(i, { ref_id: s.id, name: s.name, unit_price: s.price });
    } else if (l.kind === "product") {
      const p = products.find((x) => x.id === refId);
      if (!p) { updateBonus(i, { ref_id: null }); return; }
      updateBonus(i, { ref_id: p.id, name: p.name, unit_price: p.retail_price || p.unit_cost || 0 });
    }
  };

  const pkgBonusValue = useMemo(
    () => (pkgForm.bonus_items || []).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0),
    [pkgForm.bonus_items],
  );

  const pkgRegularTotal = useMemo(
    () => (pkgForm.items || []).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0),
    [pkgForm.items],
  );
  const pkgSavings = pkgRegularTotal - (Number(pkgForm.promo_price) || 0);

  const submitPkg = async (e) => {
    e.preventDefault();
    const cleanItems = (pkgForm.items || [])
      .filter((l) => (l.name || l.ref_id) && Number(l.quantity) > 0)
      .map((l) => ({ ...l, quantity: Number(l.quantity), unit_price: Number(l.unit_price) }));
    if (cleanItems.length === 0) { toast.error("Aggiungi almeno un servizio o prodotto"); return; }
    const cleanBonus = (pkgForm.bonus_items || []).filter((l) => l.name || l.ref_id);
    // Derive legacy service_ids for back-compat / older consumers
    const legacyServiceIds = cleanItems.filter((l) => l.kind === "service" && l.ref_id).map((l) => l.ref_id);
    const payload = {
      name: pkgForm.name,
      description: pkgForm.description,
      items: cleanItems,
      bonus_items: cleanBonus,
      service_ids: legacyServiceIds,
      promo_price: Number(pkgForm.promo_price) || 0,
      color: pkgForm.color,
    };
    try {
      if (editingPkg) await api.patch(`/packages/${editingPkg.id}`, payload);
      else await api.post("/packages", payload);
      toast.success("Percorso salvato");
      setShowPkgForm(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const removePkg = (p) => {
    setConfirmState({
      title: "Elimina percorso",
      message: <>Vuoi davvero eliminare il percorso <b>&ldquo;{p.name}&rdquo;</b>? L&apos;operazione è irreversibile.</>,
      danger: true,
      confirmLabel: "Elimina",
      onConfirm: async () => {
        try { await api.delete(`/packages/${p.id}`); load(); toast.success("Percorso eliminato"); }
        catch { toast.error("Errore"); }
      },
    });
  };

  // -- Derived state: filter + sort + group -------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (!showArchived && s.archived) return false;
      if (showArchived && !s.archived) return false;
      if (!q) return true;
      return (s.name || "").toLowerCase().includes(q)
        || (s.category || "").toLowerCase().includes(q)
        || (s.description || "").toLowerCase().includes(q);
    });
  }, [items, search, showArchived]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = a[sortField] ?? "";
      const vb = b[sortField] ?? "";
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "it", { numeric: true }) * dir;
    });
  }, [filtered, sortField, sortDir]);

  // Map category name (lowercased) → { id, color } from the service_categories collection.
  const catColorMap = useMemo(() => {
    const m = new Map();
    for (const c of categories) m.set((c.name || "").toLowerCase(), { id: c.id, color: c.color });
    return m;
  }, [categories]);
  const catColorOf = (name) => catColorMap.get((name || "").toLowerCase())?.color;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of sorted) {
      const cat = s.category?.trim() || NO_CAT;
      const color = catColorOf(cat) || s.color || "#94A3B8";
      if (!map.has(cat)) map.set(cat, { items: [], color });
      map.get(cat).items.push(s);
    }
    return Array.from(map.entries());
  }, [sorted, catColorMap]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save color for a category (upsert). If the category doc doesn't exist yet,
  // create it; then backend cascades color to all child services.
  const setCategoryColor = async (name, color) => {
    if (!name || name === NO_CAT) { setCatPickerOpen(null); return; }
    try {
      const existing = categories.find((c) => (c.name || "").toLowerCase() === name.toLowerCase());
      if (existing) {
        await api.patch(`/service-categories/${existing.id}`, { color });
      } else {
        await api.post("/service-categories", { name, color });
      }
      setCatPickerOpen(null);
      load();
      toast.success(`Colore aggiornato per "${name}"`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const activeCount = items.filter((s) => !s.archived).length;
  const archivedCount = items.filter((s) => s.archived).length;
  const existingCategories = useMemo(() => {
    const set = new Set();
    items.forEach((s) => { if (s.category?.trim()) set.add(s.category.trim()); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "it"));
  }, [items]);

  return (
    <div data-testid="services-page" className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tight inline-flex items-center gap-3">
            <Scissors className="h-7 w-7 text-[color:var(--sp-brand)]" />
            Servizi
          </h1>
          <p className="text-sm text-[color:var(--sp-text-secondary)] mt-1">
            Catalogo, percorsi e promo di {activeCount === 1 ? "1 servizio" : `${activeCount} servizi`}
            {archivedCount > 0 && <> · <button onClick={() => setShowArchived((v) => !v)} className="underline decoration-dotted hover:text-[color:var(--sp-brand)]" data-testid="services-archived-toggle">{archivedCount} archiviati</button></>}
          </p>
        </div>
        {tab === "services" ? (
          <button data-testid="services-new-btn" onClick={() => openForm()} className="sp-btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Nuovo servizio</button>
        ) : (
          <button data-testid="packages-new-btn" onClick={() => openPkgForm()} className="sp-btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /> Nuovo percorso</button>
        )}
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
          <button data-testid="tab-services" onClick={() => setTab("services")} className={`px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-2 ${tab === "services" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
            <Scissors className="h-3.5 w-3.5" /> Servizi ({activeCount})
          </button>
          <button data-testid="tab-packages" onClick={() => setTab("packages")} className={`px-4 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-2 ${tab === "packages" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}>
            <Gift className="h-3.5 w-3.5" /> Percorsi & Promo ({packages.length})
          </button>
        </div>
      </div>

      {tab === "services" && (
        <>
          <div className="sp-card p-3 flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[220px] relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--sp-text-muted)]" />
              <input
                data-testid="services-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nome, categoria, descrizione…"
                className="sp-input !pl-10 !py-1.5 text-sm"
              />
            </div>
            <SortPopover
              fields={SORT_FIELDS}
              field={sortField}
              dir={sortDir}
              onChange={(f, d) => { setSortField(f); setSortDir(d); }}
              testid="services-sort"
              defaultField="category"
              defaultDir="asc"
            />
            <div className="inline-flex rounded-full border border-[color:var(--sp-border)] bg-white p-1">
              <button
                data-testid="services-view-cards"
                onClick={() => setView("cards")}
                className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${view === "cards" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}
                title="Vista card"><LayoutGrid className="h-3.5 w-3.5" /> Card</button>
              <button
                data-testid="services-view-table"
                onClick={() => setView("table")}
                className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 ${view === "table" ? "bg-[color:var(--sp-brand)] text-white" : "text-[color:var(--sp-text-secondary)]"}`}
                title="Vista tabella"><Rows className="h-3.5 w-3.5" /> Tabella</button>
            </div>
            <button
              data-testid="services-archived-btn"
              onClick={() => setShowArchived((v) => !v)}
              className={`sp-btn-ghost inline-flex items-center gap-2 text-sm ${showArchived ? "!text-[color:var(--sp-brand)]" : ""}`}
              title="Mostra archiviati">
              <Archive className="h-4 w-4" /> {showArchived ? "Nascondi archiviati" : "Mostra archiviati"}
            </button>
          </div>

          {showArchived && (
            <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="services-archived-banner">
              Stai visualizzando i servizi <b>archiviati</b>. Puoi riattivarli o eliminarli definitivamente.
            </div>
          )}

          {sorted.length === 0 ? (
            <div className="sp-card p-12 text-center text-[color:var(--sp-text-secondary)]">
              {search ? "Nessun risultato per la ricerca." : (showArchived ? "Nessun servizio archiviato." : "Nessun servizio ancora. Aggiungi il primo servizio per iniziare.")}
            </div>
          ) : (
            grouped.map(([cat, group]) => {
              const open = !collapsed[cat];
              return (
                <section key={cat} className="space-y-2" data-testid={`services-category-${cat}`}>
                  <div className="w-full flex items-center gap-2 py-1 group relative">
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }))}
                      className="flex items-center gap-2"
                      data-testid={`services-category-toggle-${cat}`}>
                      {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    {cat !== NO_CAT ? (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setCatPickerOpen(catPickerOpen === cat ? null : cat); }}
                        className="h-4 w-4 rounded-full flex-shrink-0 ring-1 ring-[color:var(--sp-border)] hover:ring-2 hover:ring-[color:var(--sp-brand)]/60 hover:scale-110 transition-all"
                        style={{ background: group.color }}
                        title="Cambia colore della categoria"
                        data-testid={`services-category-color-${cat}`} />
                    ) : (
                      <span className="h-4 w-4 rounded-full flex-shrink-0" style={{ background: group.color }} />
                    )}
                    <button
                      type="button"
                      onClick={() => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }))}
                      className="flex items-center gap-2">
                      <span className="font-display text-lg font-black">{cat}</span>
                      <span className="text-xs text-[color:var(--sp-text-muted)]">({group.items.length})</span>
                    </button>
                    {catPickerOpen === cat && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setCatPickerOpen(null)} />
                        <div
                          className="absolute z-50 top-full left-6 mt-1.5 rounded-xl border border-[color:var(--sp-border)] bg-white shadow-xl p-3 w-[260px]"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`services-category-picker-${cat}`}>
                          <div className="text-[10px] label-eyebrow mb-2">Colore categoria</div>
                          <ColorSwatchGrid
                            value={group.color}
                            onChange={(c) => setCategoryColor(cat, c)}
                            testIdPrefix={`cat-color-${cat}`}
                          />
                          <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-2 leading-relaxed">
                            Verrà applicato a <b>tutti i {group.items.length} servizi</b> di questa categoria e all&apos;Agenda.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {open && (view === "cards"
                    ? renderCards(group.items, { openForm, archiveSvc, restoreSvc, permanentDelete, showArchived })
                    : renderTable(group.items, { openForm, archiveSvc, restoreSvc, permanentDelete, showArchived })
                  )}
                </section>
              );
            })
          )}
        </>
      )}

      {tab === "packages" && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {packages.length === 0 && (
            <div className="col-span-full sp-card p-12 text-center text-[color:var(--sp-text-secondary)]">
              <Gift className="h-8 w-8 mx-auto opacity-40 mb-3" />
              Nessun percorso o promo ancora. Combina più servizi in un pacchetto a prezzo riservato per la fidelizzazione.
            </div>
          )}
          {packages.map((p) => {
            const total = (p.items && p.items.length)
              ? p.items.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0)
              : (p.service_ids || []).reduce((s, id) => s + (items.find((x) => x.id === id)?.price || 0), 0);
            const bonusValue = (p.bonus_items || []).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
            const saving = total - (p.promo_price || 0);
            const displayItems = p.items && p.items.length
              ? p.items
              : (p.service_ids || []).map((sid) => ({ ref_id: sid, name: items.find((x) => x.id === sid)?.name || "—", quantity: 1, unit_price: items.find((x) => x.id === sid)?.price || 0, kind: "service" }));
            const displayBonus = p.bonus_items && p.bonus_items.length
              ? p.bonus_items
              : (p.bonus ? [{ kind: "other", name: p.bonus }] : []);
            return (
              <div key={p.id} data-testid={`package-card-${p.id}`} className="sp-card sp-card-hover p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 rounded-full opacity-10" style={{ background: p.color, transform: "translate(30%, -30%)" }} />
                <div className="absolute top-3 right-3 flex gap-1 z-10">
                  <button data-testid={`package-edit-${p.id}`} onClick={() => openPkgForm(p)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]"><Pencil className="h-3.5 w-3.5" /></button>
                  <button data-testid={`package-delete-${p.id}`} onClick={() => removePkg(p)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-danger)]"><X className="h-4 w-4" /></button>
                </div>
                <div className="inline-flex items-center gap-2">
                  <Gift className="h-4 w-4" style={{ color: p.color }} />
                  <span className="label-eyebrow" style={{ color: p.color }}>Percorso · Promo</span>
                </div>
                <button onClick={() => openPkgForm(p)} className="font-display text-xl font-black mt-2 hover:text-[color:var(--sp-brand)] text-left block">{p.name}</button>
                {p.description && <p className="text-sm text-[color:var(--sp-text-secondary)] mt-2 leading-relaxed line-clamp-2">{p.description}</p>}
                <div className="mt-3 space-y-1">
                  {displayItems.map((l, i) => (
                    <div key={i} className="text-xs flex items-center justify-between gap-2">
                      <span className="flex items-center gap-1.5 truncate">
                        {l.kind === "product" ? <PackageIcon className="h-3 w-3 text-[color:var(--sp-text-muted)]" /> : <Scissors className="h-3 w-3 text-[color:var(--sp-text-muted)]" />}
                        <span className="truncate">{l.name}</span>
                        {l.quantity > 1 && <span className="text-[10px] font-bold text-[color:var(--sp-text-muted)]">×{l.quantity}</span>}
                      </span>
                      <span className="text-[10px] text-[color:var(--sp-text-muted)] tabular-nums">€ {(Number(l.unit_price) * Number(l.quantity)).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {displayBonus.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-dashed border-[color:var(--sp-border)] space-y-1">
                    <div className="text-[10px] label-eyebrow inline-flex items-center gap-1"><Sparkles className="h-3 w-3" style={{ color: p.color }} /> In omaggio</div>
                    {displayBonus.map((b, i) => (
                      <div key={i} className="text-xs italic text-[color:var(--sp-text-muted)] flex items-center justify-between gap-2">
                        <span className="truncate">· {b.name}{b.quantity > 1 ? <span className="ml-1 text-[10px] font-bold">×{b.quantity}</span> : null}</span>
                        {b.unit_price > 0 && <span className="text-[10px] tabular-nums whitespace-nowrap">€ {(Number(b.unit_price) * Number(b.quantity || 1)).toFixed(2)}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <div className="mt-4 pt-4 border-t border-[color:var(--sp-border)] flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] label-eyebrow">Prezzo riservato</div>
                    <div className="text-2xl font-display font-black" style={{ color: p.color }}>€ {p.promo_price?.toFixed(2)}</div>
                  </div>
                  <div className="text-right space-y-0.5">
                    {saving > 0 && (
                      <>
                        <div className="text-xs text-[color:var(--sp-text-muted)] line-through">€ {total.toFixed(2)}</div>
                        <div className="text-[11px] font-black text-[color:var(--sp-success)]">Risparmi € {saving.toFixed(2)}</div>
                      </>
                    )}
                    {bonusValue > 0 && (
                      <div className="text-[11px] font-black text-[color:var(--sp-brand)] inline-flex items-center gap-1"><Sparkles className="h-3 w-3" />+ € {bonusValue.toFixed(2)} bonus</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Service form */}
      {showForm && (
        <div className="fixed inset-0 z-30 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <div className="sp-card w-full max-w-md p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black">{editing ? "Modifica servizio" : "Nuovo servizio"}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div><label className="text-sm font-bold">Nome servizio</label><input data-testid="service-name-input" required placeholder="es. Taglio + Piega" className="sp-input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div>
                <label className="text-sm font-bold">Categoria</label>
                <CategoryCombobox
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  options={existingCategories}
                  testId="service-category-input"
                />
                <p className="text-[10px] text-[color:var(--sp-text-muted)] mt-1">Scegli una categoria esistente o scrivine una nuova.</p>
              </div>
              <div><label className="text-sm font-bold">Descrizione</label><textarea data-testid="service-description-input" placeholder="Descrizione del servizio" className="sp-input mt-1 min-h-[90px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="text-sm font-bold">Durata (min)</label><input data-testid="service-duration-input" required type="number" min="5" className="sp-input mt-1" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></div>
                <div>
                  <label className="text-sm font-bold">Tipo prezzo</label>
                  <select data-testid="service-price-type" className="sp-input mt-1" value={form.price_type} onChange={(e) => setForm({ ...form, price_type: e.target.value })}>
                    <option value="fixed">Fisso</option>
                    <option value="from">Da…</option>
                  </select>
                </div>
                <div><label className="text-sm font-bold">Prezzo (€)</label><input data-testid="service-price-input" required type="number" step="0.01" className="sp-input mt-1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} /></div>
              </div>
              {form.price_type === "from" && (
                <div className="text-[11px] rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40 p-2 text-[color:var(--sp-text-secondary)]">
                  Il prezzo sarà mostrato come <b>&ldquo;Da € {form.price || "…"}&rdquo;</b> in catalogo e prenotazioni.
                </div>
              )}
              <div className="text-[11px] rounded-lg border border-[color:var(--sp-border)] bg-[color:var(--sp-subtle)]/40 p-2 text-[color:var(--sp-text-secondary)]">
                Il colore del servizio è ereditato dalla <b>categoria</b>. Modificalo cliccando sul pallino accanto al nome della categoria nel catalogo.
              </div>
              <button data-testid="service-submit-btn" className="sp-btn-primary w-full">Salva</button>
            </form>
          </div>
        </div>
      )}

      {/* Package form (cart-style) */}
      {showPkgForm && (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowPkgForm(false)}>
          <div className="sp-card w-full max-w-3xl p-8 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-2xl font-black inline-flex items-center gap-2"><Gift className="h-5 w-5 text-[color:var(--sp-brand)]" /> {editingPkg ? "Modifica percorso" : "Nuovo percorso"}</h3>
              <button onClick={() => setShowPkgForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={submitPkg} className="space-y-4">
              <div>
                <label className="text-sm font-bold">Nome percorso</label>
                <input data-testid="package-name-input" required placeholder="es. Percorso Cheratina" className="sp-input mt-1" value={pkgForm.name} onChange={(e) => setPkgForm({ ...pkgForm, name: e.target.value })} />
              </div>
              <div><label className="text-sm font-bold">Descrizione</label><textarea data-testid="package-description-input" placeholder="Cosa comprende, per chi è pensato…" className="sp-input mt-1 min-h-[60px]" value={pkgForm.description} onChange={(e) => setPkgForm({ ...pkgForm, description: e.target.value })} /></div>

              {/* Cart-style items table */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold">Servizi e prodotti inclusi</label>
                  <div className="flex gap-1.5">
                    <button type="button" data-testid="pkg-add-service" onClick={() => addPkgItem("service")} className="text-xs px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] inline-flex items-center gap-1"><Scissors className="h-3 w-3" /> + Servizio</button>
                    <button type="button" data-testid="pkg-add-product" onClick={() => addPkgItem("product")} className="text-xs px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] inline-flex items-center gap-1"><PackageIcon className="h-3 w-3" /> + Prodotto</button>
                  </div>
                </div>
                <div className="rounded-lg border border-[color:var(--sp-border)]">
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[color:var(--sp-subtle)] label-eyebrow text-[10px] rounded-t-lg">
                    <div className="col-span-6">Elemento</div>
                    <div className="col-span-1 text-right">Qt.</div>
                    <div className="col-span-2 text-right">Prezzo</div>
                    <div className="col-span-2 text-right">Totale</div>
                    <div className="col-span-1"></div>
                  </div>
                  {pkgForm.items.length === 0 && (
                    <div className="px-3 py-6 text-xs text-center text-[color:var(--sp-text-muted)]">Aggiungi almeno un servizio o prodotto.</div>
                  )}
                  {pkgForm.items.map((l, i) => {
                    const lineTotal = (Number(l.quantity) || 0) * (Number(l.unit_price) || 0);
                    const opts = l.kind === "service"
                      ? items.filter((s) => !s.archived).map((s) => ({ id: s.id, label: s.name, sub: `€ ${s.price}`, color: s.color }))
                      : products.map((p) => ({ id: p.id, label: p.name, sub: `€ ${p.retail_price || 0} · stock: ${p.quantity ?? 0}` }));
                    return (
                      <div key={i} data-testid={`pkg-item-${i}`} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[color:var(--sp-border)] items-center">
                        <div className="col-span-6">
                          <SearchableSelect
                            testId={`pkg-item-select-${i}`}
                            size="sm"
                            value={l.ref_id || ""}
                            onChange={(v) => onPkgItemPick(i, v)}
                            options={opts}
                            placeholder={l.kind === "service" ? "— Scegli un servizio —" : "— Scegli un prodotto —"}
                          />
                        </div>
                        <div className="col-span-1">
                          <input
                            data-testid={`pkg-item-qty-${i}`}
                            type="text"
                            inputMode="numeric"
                            value={l.quantity}
                            onChange={(e) => updatePkgItem(i, { quantity: e.target.value.replace(/[^0-9]/g, "") || 0 })}
                            className="sp-input !py-1 text-xs text-right tabular-nums"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            data-testid={`pkg-item-price-${i}`}
                            type="text"
                            inputMode="decimal"
                            value={l.unit_price}
                            onChange={(e) => updatePkgItem(i, { unit_price: e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })}
                            className="sp-input !py-1 text-xs text-right tabular-nums"
                          />
                        </div>
                        <div className="col-span-2 text-right text-xs font-bold tabular-nums whitespace-nowrap">€ {lineTotal.toFixed(2)}</div>
                        <div className="col-span-1 text-right">
                          <button type="button" data-testid={`pkg-item-remove-${i}`} onClick={() => removePkgItem(i)} className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[color:var(--sp-subtle)]/40 border-t border-[color:var(--sp-border)] items-center">
                    <div className="col-span-9 text-xs font-bold text-right">Totale listino</div>
                    <div className="col-span-2 text-right text-sm font-black tabular-nums" data-testid="pkg-listino-total">€ {pkgRegularTotal.toFixed(2)}</div>
                    <div className="col-span-1"></div>
                  </div>
                </div>
              </div>

              {/* Bonus / vantaggi */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-bold inline-flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5 text-[color:var(--sp-brand)]" /> Bonus / vantaggi in omaggio</label>
                  <div className="flex gap-1.5">
                    <button type="button" data-testid="bonus-add-service" onClick={() => addBonus("service")} className="text-xs px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] inline-flex items-center gap-1"><Scissors className="h-3 w-3" /> + Servizio</button>
                    <button type="button" data-testid="bonus-add-product" onClick={() => addBonus("product")} className="text-xs px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] inline-flex items-center gap-1"><PackageIcon className="h-3 w-3" /> + Prodotto</button>
                    <button type="button" data-testid="bonus-add-other" onClick={() => addBonus("other")} className="text-xs px-2.5 py-1 rounded-full border border-[color:var(--sp-border)] hover:bg-[color:var(--sp-subtle)] inline-flex items-center gap-1">+ Testo libero</button>
                  </div>
                </div>
                {pkgForm.bonus_items.length === 0 ? (
                  <div className="text-[11px] text-[color:var(--sp-text-muted)] px-3 py-2">Nessun bonus. Aggiungi omaggi per rendere il pacchetto irresistibile.</div>
                ) : (
                  <div className="rounded-lg border border-[color:var(--sp-border)]">
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[color:var(--sp-subtle)] label-eyebrow text-[10px] rounded-t-lg">
                      <div className="col-span-6">Omaggio</div>
                      <div className="col-span-1 text-right">Qt.</div>
                      <div className="col-span-2 text-right">Valore</div>
                      <div className="col-span-2 text-right">Totale</div>
                      <div className="col-span-1"></div>
                    </div>
                    {pkgForm.bonus_items.map((b, i) => {
                      const opts = b.kind === "service"
                        ? items.filter((s) => !s.archived).map((s) => ({ id: s.id, label: s.name, color: s.color }))
                        : (b.kind === "product" ? products.map((p) => ({ id: p.id, label: p.name })) : []);
                      const lineValue = (Number(b.quantity) || 0) * (Number(b.unit_price) || 0);
                      return (
                        <div key={i} data-testid={`bonus-item-${i}`} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-[color:var(--sp-border)] items-center">
                          <div className="col-span-6">
                            {b.kind === "other" ? (
                              <input
                                data-testid={`bonus-text-${i}`}
                                value={b.name}
                                onChange={(e) => updateBonus(i, { name: e.target.value })}
                                placeholder="es. Kit home care, consulenza…"
                                className="sp-input !py-1 text-xs"
                              />
                            ) : (
                              <SearchableSelect
                                testId={`bonus-select-${i}`}
                                size="sm"
                                value={b.ref_id || ""}
                                onChange={(v) => onBonusPick(i, v)}
                                options={opts}
                                placeholder={b.kind === "service" ? "— Servizio in omaggio —" : "— Prodotto in omaggio —"}
                              />
                            )}
                          </div>
                          <div className="col-span-1">
                            <input
                              data-testid={`bonus-qty-${i}`}
                              type="text"
                              inputMode="numeric"
                              value={b.quantity}
                              onChange={(e) => updateBonus(i, { quantity: e.target.value.replace(/[^0-9]/g, "") || 0 })}
                              className="sp-input !py-1 text-xs text-right tabular-nums"
                            />
                          </div>
                          <div className="col-span-2">
                            <input
                              data-testid={`bonus-price-${i}`}
                              type="text"
                              inputMode="decimal"
                              value={b.unit_price}
                              onChange={(e) => updateBonus(i, { unit_price: e.target.value.replace(/[^0-9.,]/g, "").replace(",", ".") })}
                              className="sp-input !py-1 text-xs text-right tabular-nums"
                              placeholder="0"
                            />
                          </div>
                          <div className="col-span-2 text-right text-xs font-bold tabular-nums whitespace-nowrap">€ {lineValue.toFixed(2)}</div>
                          <div className="col-span-1 text-right">
                            <button type="button" data-testid={`bonus-remove-${i}`} onClick={() => removeBonus(i)} className="p-1 text-[color:var(--sp-text-muted)] hover:text-[color:var(--sp-danger)]"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                      );
                    })}
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[color:var(--sp-brand)]/5 border-t border-[color:var(--sp-border)] items-center">
                      <div className="col-span-9 text-xs font-bold text-right">Valore totale bonus (in omaggio)</div>
                      <div className="col-span-2 text-right text-sm font-black tabular-nums text-[color:var(--sp-brand)]" data-testid="pkg-bonus-total">€ {pkgBonusValue.toFixed(2)}</div>
                      <div className="col-span-1"></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Prezzo riservato + colore */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-[color:var(--sp-border)]">
                <div>
                  <label className="text-sm font-bold">Prezzo riservato (€)</label>
                  <input data-testid="package-price-input" required type="number" step="0.01" min="0" className="sp-input mt-1" value={pkgForm.promo_price} onChange={(e) => setPkgForm({ ...pkgForm, promo_price: e.target.value })} />
                  {pkgSavings > 0 && <div className="text-xs font-black text-[color:var(--sp-success)] mt-1" data-testid="pkg-savings">Sconto di € {pkgSavings.toFixed(2)} sul listino</div>}
                  {pkgBonusValue > 0 && <div className="text-xs font-black text-[color:var(--sp-brand)] mt-0.5">+ € {pkgBonusValue.toFixed(2)} di bonus in omaggio</div>}
                  {pkgSavings < 0 && Number(pkgForm.promo_price) > 0 && <div className="text-xs text-[color:var(--sp-danger)] mt-1">Prezzo riservato maggiore del listino</div>}
                </div>
                <div>
                  <label className="text-sm font-bold">Colore percorso</label>
                  <ColorSwatchGrid
                    value={pkgForm.color}
                    onChange={(c) => setPkgForm({ ...pkgForm, color: c })}
                    testIdPrefix="package-color"
                    className="mt-2"
                  />
                </div>
              </div>

              <button data-testid="package-submit-btn" className="sp-btn-primary w-full">{editingPkg ? "Salva percorso" : "Crea percorso"}</button>
            </form>
          </div>
        </div>
      )}

      {/* Confirm dialog — reused for permanent delete of service and package */}
      {confirmState && (
        <ConfirmDialog
          {...confirmState}
          onClose={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentational helpers
// ---------------------------------------------------------------------------
function formatPrice(s) {
  const prefix = s.price_type === "from" ? "da " : "";
  return `${prefix}€ ${Number(s.price).toFixed(2)}`;
}

function renderCards(list, { openForm, archiveSvc, restoreSvc, permanentDelete, showArchived }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {list.map((s) => (
        <div key={s.id} data-testid={`service-card-${s.id}`}
             className={`sp-card sp-card-hover p-6 relative ${s.archived ? "opacity-70" : ""}`}>
          <div className="absolute top-3 right-3 flex gap-1">
            {showArchived ? (
              <>
                <button data-testid={`service-restore-${s.id}`} onClick={() => restoreSvc(s)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]" title="Riattiva"><ArchiveRestore className="h-4 w-4" /></button>
                <button data-testid={`service-delete-${s.id}`} onClick={() => permanentDelete(s)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-danger)]" title="Elimina definitivamente"><Trash2 className="h-4 w-4" /></button>
              </>
            ) : (
              <>
                <button data-testid={`service-edit-${s.id}`} onClick={() => openForm(s)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-brand)]" title="Modifica"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`service-archive-${s.id}`} onClick={() => archiveSvc(s)} className="p-1 text-[color:var(--sp-text-secondary)] hover:text-[color:var(--sp-danger)]" title="Archivia"><Archive className="h-4 w-4" /></button>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ background: s.color || "#06B6D4" }} />
            <div className="label-eyebrow">{s.category || "Servizio"}</div>
            {s.price_type === "from" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-black uppercase tracking-wider">Da…</span>}
          </div>
          <button onClick={() => (showArchived ? restoreSvc(s) : openForm(s))} className="font-display text-xl font-black mt-2 hover:text-[color:var(--sp-brand)] text-left block">{s.name}</button>
          {s.description && <p className="text-sm text-[color:var(--sp-text-secondary)] mt-2 leading-relaxed line-clamp-3">{s.description}</p>}
          <div className="flex items-baseline justify-between mt-4">
            <span className="text-2xl font-display font-black text-gradient" data-testid={`service-price-${s.id}`}>{formatPrice(s)}</span>
            <span className="text-sm text-[color:var(--sp-text-secondary)]">{s.duration_min} min</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function renderTable(list, { openForm, archiveSvc, restoreSvc, permanentDelete, showArchived }) {
  return (
    <div className="sp-card overflow-hidden">
      <table className="w-full text-sm" data-testid="services-table">
        <thead className="bg-[color:var(--sp-subtle)] label-eyebrow text-[10px]">
          <tr>
            <th className="text-left px-3 py-2">Servizio</th>
            <th className="text-left px-3 py-2">Descrizione</th>
            <th className="text-right px-3 py-2">Durata</th>
            <th className="text-right px-3 py-2">Prezzo</th>
            <th className="text-right px-3 py-2 w-24"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((s) => (
            <tr key={s.id} data-testid={`service-row-${s.id}`}
                className={`border-t border-[color:var(--sp-border)]/40 hover:bg-[color:var(--sp-subtle)]/40 ${s.archived ? "opacity-70" : ""}`}>
              <td className="px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: s.color || "#06B6D4" }} />
                  <button onClick={() => (showArchived ? restoreSvc(s) : openForm(s))} className="font-bold text-left hover:text-[color:var(--sp-brand)]">{s.name}</button>
                  {s.price_type === "from" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-black uppercase">Da…</span>}
                </div>
              </td>
              <td className="px-3 py-2 text-[color:var(--sp-text-secondary)] text-xs truncate max-w-md">{s.description || "—"}</td>
              <td className="px-3 py-2 text-right tabular-nums">{s.duration_min} min</td>
              <td className="px-3 py-2 text-right font-bold tabular-nums whitespace-nowrap" data-testid={`service-row-price-${s.id}`}>{formatPrice(s)}</td>
              <td className="px-3 py-2 text-right">
                <div className="inline-flex gap-0.5">
                  {showArchived ? (
                    <>
                      <button data-testid={`service-row-restore-${s.id}`} onClick={() => restoreSvc(s)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)] text-[color:var(--sp-brand)]" title="Riattiva"><ArchiveRestore className="h-4 w-4" /></button>
                      <button data-testid={`service-row-delete-${s.id}`} onClick={() => permanentDelete(s)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-danger)]" title="Elimina definitivamente"><Trash2 className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <>
                      <button data-testid={`service-row-edit-${s.id}`} onClick={() => openForm(s)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)]" title="Modifica"><Pencil className="h-3.5 w-3.5" /></button>
                      <button data-testid={`service-row-archive-${s.id}`} onClick={() => archiveSvc(s)} className="p-1.5 rounded hover:bg-[color:var(--sp-subtle)] hover:text-[color:var(--sp-danger)]" title="Archivia"><Archive className="h-4 w-4" /></button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CategoryCombobox — input with autocomplete dropdown of existing categories.
// Users can pick an existing category or type a brand-new one.
// ---------------------------------------------------------------------------
function CategoryCombobox({ value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, value]);

  const showCreate = value && !options.some((o) => o.toLowerCase() === value.toLowerCase());

  return (
    <div ref={wrapRef} className="relative">
      <input
        data-testid={testId}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        placeholder="es. Taglio, Colore, Trattamento"
        className="sp-input mt-1"
      />
      {open && (filtered.length > 0 || showCreate) && (
        <div className="absolute z-40 mt-1 w-full rounded-lg border border-[color:var(--sp-border)] bg-white shadow-lg overflow-hidden max-h-56 overflow-y-auto" data-testid={testId ? `${testId}-dropdown` : undefined}>
          {filtered.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange(o); setOpen(false); }}
              data-testid={testId ? `${testId}-option-${o}` : undefined}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-[color:var(--sp-subtle)]"
            >{o}</button>
          ))}
          {showCreate && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={testId ? `${testId}-create` : undefined}
              className="w-full text-left px-3 py-1.5 text-xs border-t border-[color:var(--sp-border)] bg-[color:var(--sp-brand)]/5 text-[color:var(--sp-brand)] font-bold"
            >+ Crea &ldquo;{value}&rdquo;</button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConfirmDialog — in-app modal for destructive actions (replaces window.confirm)
// ---------------------------------------------------------------------------
function ConfirmDialog({ title, message, confirmLabel = "Conferma", danger, onConfirm, onClose }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try { await onConfirm(); onClose(); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="confirm-dialog">
      <div className="sp-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-xl font-black mb-3">{title}</h3>
        <div className="text-sm text-[color:var(--sp-text-secondary)] leading-relaxed">{message}</div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" data-testid="confirm-cancel" onClick={onClose} className="sp-btn-ghost !py-1.5 !px-3 text-xs">Annulla</button>
          <button
            type="button"
            data-testid="confirm-ok"
            disabled={busy}
            onClick={handle}
            className={`!py-1.5 !px-3 text-xs font-bold rounded-full ${danger ? "bg-[color:var(--sp-danger)] text-white hover:opacity-90" : "sp-btn-primary"}`}
          >
            {busy ? "Attendere…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
